import addCalendarRouteHandler from './addCalendarRouteHandler';
import browseDiscoverCalendarsRouteHandler from './browseDiscoverCalendarsRouteHandler';
import { composeRouteHandler, editRouteHandler, itemRouteHandler } from './formsRouteHandlers';
import getCurrentRoute from './getCurrentRoute';
import groupCalendarAddEventsRouteHandler from './groupCalendarAddEventsRouteHandler';
import groupCalendarRouteHandler from './groupCalendarRouteHandler';
import peekRouteHandler from './peekRouteHandler';
import routeHandler from './routeHandler';
import searchRouteHandler from './searchRouteHandler';
import viewRouteHandler from './viewRouteHandler';
import { CalendarViewRouteType } from 'owa-calendar-module-store';
import { initializeOptionRoutes } from 'owa-options-router';
import { registerRoute as registerRouteFunction, RouterOptions } from 'owa-router';
import {
    ADD_FROM_WEB_KEYWORD,
    ADD_CALENDAR_ROUTE_KEYWORD,
    TEAMSNAP_ROUTE_KEYWORD,
    BROWSE_DISCOVER_CALENDARS_ROUTE_KEYWORD,
    GROUP_CALENDAR_ROUTE_KEYWORD,
    ITEM_ROUTE_KEYWORD,
    SEARCH_ROUTE_KEYWORD,
    VIEW_ROUTE_KEYWORD,
    COMPOSE_ROUTE_KEYWORD,
    EDIT_ROUTE_KEYWORD,
    ADD_GROUP_EVENTS_ROUTE_KEYWORD,
    SCHOOLS_ROUTE_KEYWORD,
    PERSONAL_ACCOUNT_ROUTE_KEYWORD,
} from '../constants';

export let routerOptions: RouterOptions = {
    routeGenerator: Promise.resolve(getCurrentRoute),
    defaultRoute: '/',
};

/** Special value used to indicate that we should load user's preferred calendar view type */
export const USER_CALENDAR_VIEW_TYPE_ROUTE = 'DEFAULT_VIEW';

/** Extended enum for handling concerns specific to module bootstrap */
export type CalendarViewRouteTypeExtended =
    | CalendarViewRouteType
    | typeof USER_CALENDAR_VIEW_TYPE_ROUTE;

// TODO: VSO #93977 Rationalize module routing when canvas view type is explicitly specified for surface-only routes

export interface CalendarViewRouteParameters {
    view: CalendarViewRouteTypeExtended;
    y?: number;
    m?: number;
    d?: number;
    teams?: string;
    forceSurfaceViewType?: boolean;
}

export interface GroupCalendarViewRouteParameters extends CalendarViewRouteParameters {
    groupDomain: string;
    groupAlias: string;
}

export interface PeekRouteParameters {
    itemId: string;
}

export interface ItemRouteParameters extends PeekRouteParameters {
    showMaster?: string;
}

export interface CalendarSearchRouteParameters {
    query: string;
}

export function initializeRoutes(registerRoute: typeof registerRouteFunction) {
    const viewBaseRoute = `${VIEW_ROUTE_KEYWORD}/:view`;
    const viewFullRoute = `${VIEW_ROUTE_KEYWORD}/:view/y/:y/m/:m/d/:d`;

    // Path: /item/:itemId
    registerRoute(`/${ITEM_ROUTE_KEYWORD}/:itemId`, itemRouteHandler);

    // Path: /item/:itemId/:showMaster
    registerRoute(`/${ITEM_ROUTE_KEYWORD}/:itemId/:showMaster`, itemRouteHandler);

    // Path: /edit/:itemId
    registerRoute(`/${EDIT_ROUTE_KEYWORD}/:itemId`, editRouteHandler);

    // Path: /edit/:itemId/:showMaster
    registerRoute(`/${EDIT_ROUTE_KEYWORD}/:itemId/:eventScope`, editRouteHandler);

    // Path: /action/compose
    registerRoute(`/action/${COMPOSE_ROUTE_KEYWORD}`, composeRouteHandler);

    // Path: /view/:view/:teams
    registerRoute(`/${viewBaseRoute}/:teams`, viewRouteHandler);

    // Path: /view/:view/y/:y/m/:m/d/:d/:teams
    registerRoute(`/${viewFullRoute}/:teams`, viewRouteHandler);

    // Path: /view/:view
    registerRoute(`/${viewBaseRoute}`, viewRouteHandler);

    // Path: /view/:view/y/:y/m/:m/d/:d
    registerRoute(`/${viewFullRoute}`, viewRouteHandler);

    // Path: /addcalendar
    registerRoute(`/${ADD_CALENDAR_ROUTE_KEYWORD}`, addCalendarRouteHandler);

    // Path: /addcalendar/teamsnap
    registerRoute(
        `/${ADD_CALENDAR_ROUTE_KEYWORD}/${TEAMSNAP_ROUTE_KEYWORD}`,
        addCalendarRouteHandler
    );

    // Path: /addcalendar/schools
    registerRoute(
        `/${ADD_CALENDAR_ROUTE_KEYWORD}/${SCHOOLS_ROUTE_KEYWORD}`,
        addCalendarRouteHandler
    );

    // Path: /addcalendar/personal
    registerRoute(
        `/${ADD_CALENDAR_ROUTE_KEYWORD}/${PERSONAL_ACCOUNT_ROUTE_KEYWORD}`,
        addCalendarRouteHandler
    );

    // Path: /addcalendar/addfromweb
    registerRoute(
        `/${ADD_CALENDAR_ROUTE_KEYWORD}/${ADD_FROM_WEB_KEYWORD}`,
        addCalendarRouteHandler
    );

    // Path: /browseDiscoverCalendars
    registerRoute(
        `/${BROWSE_DISCOVER_CALENDARS_ROUTE_KEYWORD}`,
        browseDiscoverCalendarsRouteHandler
    );

    // Path: /addfromweb
    registerRoute(`/${ADD_FROM_WEB_KEYWORD}`, browseDiscoverCalendarsRouteHandler);

    // Path: /addGroupEvents
    registerRoute(`/${ADD_GROUP_EVENTS_ROUTE_KEYWORD}`, groupCalendarAddEventsRouteHandler);

    registerRoute('/showPeek/item/:itemId', peekRouteHandler);

    // Path: /group/:groupDomain/:groupAlias
    registerRoute(
        `/${GROUP_CALENDAR_ROUTE_KEYWORD}/:groupDomain/:groupAlias`,
        groupCalendarRouteHandler
    );

    // Path: /group/:groupDomain/:groupAlias/view/:view
    registerRoute(
        `/${GROUP_CALENDAR_ROUTE_KEYWORD}/:groupDomain/:groupAlias/${viewBaseRoute}`,
        groupCalendarRouteHandler
    );

    // Path: /group/:groupDomain/:groupAlias/view/:view/y/:y/m/:m/d/:d
    registerRoute(
        `/${GROUP_CALENDAR_ROUTE_KEYWORD}/:groupDomain/:groupAlias/${viewFullRoute}`,
        groupCalendarRouteHandler
    );

    // Path: /search/:query
    registerRoute(`/${SEARCH_ROUTE_KEYWORD}/:query`, searchRouteHandler);

    registerRoute('/', routeHandler);

    initializeOptionRoutes();
    // TODO: fill in more routes for calendar

    return Promise.resolve();
}
